import React, { useState } from 'react';

// Define types for our component
interface CsvToSqlProps {}

// CSV to SQL conversion function
const csvToSql = (
  csv: string, 
  options: {
    tableName: string;
    header: boolean;
    delimiter: string;
    dynamicTyping: boolean;
    trimValues: boolean;
    skipEmptyLines: boolean;
    includeCreateTable: boolean;
    singleStatement: boolean;
    sqlDialect: string;
  }
): string => {
  try {
    // Split the CSV by lines
    const lines = csv.split(/\r?\n/);
    
    // Handle empty CSV
    if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
      return '';
    }
    
    // Parse the headers if they exist
    let headers: string[] = [];
    let startRow = 0;
    
    if (options.header) {
      // Use the first row as headers
      const headerRow = lines[0];
      headers = headerRow
        .split(options.delimiter)
        .map(header => options.trimValues ? header.trim() : header)
        // Remove any special characters from headers that aren't SQL-friendly
        .map(header => header.replace(/[^\w_]/g, '_').replace(/^[0-9]/, '_$&'));
      
      startRow = 1;
    } else {
      // Generate default column names
      const firstRow = lines[0].split(options.delimiter);
      headers = firstRow.map((_, index) => `column${index + 1}`);
    }
    
    // Determine column types if CREATE TABLE is requested
    let columnTypes: string[] = [];
    
    if (options.includeCreateTable) {
      columnTypes = headers.map(() => 'TEXT'); // Default type
      
      // If dynamic typing is enabled, try to guess column types
      if (options.dynamicTyping) {
        // Analyze sample data to determine types
        const sampleSize = Math.min(lines.length - startRow, 10);
        const samples: string[][] = [];
        
        for (let i = startRow; i < startRow + sampleSize; i++) {
          if (i >= lines.length) break;
          const line = lines[i].trim();
          if (options.skipEmptyLines && line === '') continue;
          
          const values = parseCSVLine(line, options.delimiter);
          if (values.length > 0) {
            samples.push(values);
          }
        }
        
        // Determine column types based on samples
        for (let j = 0; j < headers.length; j++) {
          let allNumbers = true;
          let allIntegers = true;
          let allBooleans = true;
          let allDates = true;
          let allNulls = true;
          
          for (let i = 0; i < samples.length; i++) {
            const value = j < samples[i].length ? samples[i][j] : '';
            const trimmedValue = options.trimValues ? value.trim() : value;
            
            // Skip empty values
            if (trimmedValue === '') continue;
            
            allNulls = false;
            
            // Check for non-numeric values
            if (!/^-?\d+(\.\d+)?$/.test(trimmedValue)) {
              allNumbers = false;
              allIntegers = false;
            }
            
            // Check for non-integer values
            if (allIntegers && /^-?\d+\.\d+$/.test(trimmedValue)) {
              allIntegers = false;
            }
            
            // Check for non-boolean values
            if (allBooleans && !['true', 'false', '1', '0'].includes(trimmedValue.toLowerCase())) {
              allBooleans = false;
            }
            
            // Check for non-date values
            if (allDates && !/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{3})?Z?)?$/.test(trimmedValue)) {
              allDates = false;
            }
          }
          
          if (allNulls) {
            columnTypes[j] = 'TEXT'; // Default to TEXT for all nulls
          } else if (allIntegers) {
            columnTypes[j] = 'INTEGER';
          } else if (allNumbers) {
            columnTypes[j] = 'NUMERIC';
          } else if (allDates) {
            columnTypes[j] = options.sqlDialect === 'mysql' ? 'DATETIME' : 'TIMESTAMP';
          } else if (allBooleans) {
            columnTypes[j] = options.sqlDialect === 'postgresql' ? 'BOOLEAN' : 'INTEGER';
          } else {
            // Default to TEXT
            columnTypes[j] = 'TEXT';
          }
        }
      }
    }
    
    // Generate SQL
    let sql = '';
    
    // Generate CREATE TABLE statement if requested
    if (options.includeCreateTable) {
      let createTableSql = '';
      
      switch (options.sqlDialect) {
        case 'mysql':
          createTableSql = `CREATE TABLE IF NOT EXISTS \`${options.tableName}\` (\n`;
          break;
        case 'postgresql':
          createTableSql = `CREATE TABLE IF NOT EXISTS "${options.tableName}" (\n`;
          break;
        default: // SQLite and others
          createTableSql = `CREATE TABLE IF NOT EXISTS [${options.tableName}] (\n`;
      }
      
      headers.forEach((header, index) => {
        let columnDef = '';
        
        switch (options.sqlDialect) {
          case 'mysql':
            columnDef = `  \`${header}\` ${columnTypes[index]}`;
            break;
          case 'postgresql':
            columnDef = `  "${header}" ${columnTypes[index]}`;
            break;
          default: // SQLite and others
            columnDef = `  [${header}] ${columnTypes[index]}`;
        }
        
        createTableSql += columnDef;
        if (index < headers.length - 1) {
          createTableSql += ',\n';
        } else {
          createTableSql += '\n';
        }
      });
      
      createTableSql += ');\n\n';
      sql += createTableSql;
    }
    
    // Process rows into INSERT statements
    const insertStatements: string[] = [];
    
    for (let i = startRow; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines if configured to do so
      if (options.skipEmptyLines && line === '') {
        continue;
      }
      
      const values = parseCSVLine(line, options.delimiter);
      
      if (values.length > 0) {
        let insertSql = '';
        
        switch (options.sqlDialect) {
          case 'mysql':
            insertSql = `INSERT INTO \`${options.tableName}\` (${headers.map(h => `\`${h}\``).join(', ')}) VALUES (`;
            break;
          case 'postgresql':
            insertSql = `INSERT INTO "${options.tableName}" (${headers.map(h => `"${h}"`).join(', ')}) VALUES (`;
            break;
          default: // SQLite and others
            insertSql = `INSERT INTO [${options.tableName}] (${headers.map(h => `[${h}]`).join(', ')}) VALUES (`;
        }
        
        const valuesSql: string[] = [];
        
        for (let j = 0; j < headers.length; j++) {
          const value = j < values.length ? values[j] : '';
          const trimmedValue = options.trimValues ? value.trim() : value;
          
          if (options.dynamicTyping) {
            const typedValue = convertValueForSql(trimmedValue, options.sqlDialect);
            valuesSql.push(typedValue);
          } else {
            valuesSql.push(escapeSqlString(trimmedValue, options.sqlDialect));
          }
        }
        
        insertSql += valuesSql.join(', ') + ')';
        
        if (!options.singleStatement) {
          insertSql += ';';
        }
        
        insertStatements.push(insertSql);
      }
    }
    
    if (options.singleStatement && insertStatements.length > 0) {
      sql += insertStatements.join(',\n') + ';';
    } else {
      sql += insertStatements.join('\n');
    }
    
    return sql;
  } catch (error) {
    throw error;
  }
};

// Helper function to parse a CSV line respecting quoted values
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Handle escaped double quotes
      if (i + 1 < line.length && line[i + 1] === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle the quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  return values;
};

// Helper function to escape SQL strings based on dialect
const escapeSqlString = (value: string, dialect: string): string => {
  // Return NULL for empty values
  if (value === '') {
    return 'NULL';
  }
  
  // Escape single quotes based on SQL dialect
  let escaped = '';
  
  switch (dialect) {
    case 'mysql':
      escaped = `'${value.replace(/'/g, "\\'")}'`;
      break;
    case 'postgresql':
    case 'sqlserver':
      escaped = `'${value.replace(/'/g, "''")}'`;
      break;
    default: // SQLite and others
      escaped = `'${value.replace(/'/g, "''")}'`;
  }
  
  return escaped;
};

// Helper function to convert values to SQL data types
const convertValueForSql = (value: string, dialect: string): string => {
  // Empty values
  if (value === '') {
    return 'NULL';
  }
  
  // Boolean values
  if (value.toLowerCase() === 'true' || value === '1') {
    if (dialect === 'postgresql') {
      return 'TRUE';
    } else {
      return '1';
    }
  }
  
  if (value.toLowerCase() === 'false' || value === '0') {
    if (dialect === 'postgresql') {
      return 'FALSE';
    } else {
      return '0';
    }
  }
  
  // Null value
  if (value.toLowerCase() === 'null' || value.toLowerCase() === 'n/a') {
    return 'NULL';
  }
  
  // Number values
  if (/^-?\d+$/.test(value) || /^-?\d+\.\d+$/.test(value)) {
    return value;
  }
  
  // Date values (ISO format)
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{3})?Z?)?$/.test(value)) {
    return escapeSqlString(value, dialect);
  }
  
  // Default to string
  return escapeSqlString(value, dialect);
};

// Main component to convert CSV to SQL
const CsvToSql: React.FC<CsvToSqlProps> = () => {
  // State for the CSV input
  const [csvInput, setCsvInput] = useState<string>('');
  // State for the SQL output
  const [sqlOutput, setSqlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the table name
  const [tableName, setTableName] = useState<string>('my_table');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isSqlGenerated, setIsSqlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  
  // Conversion options
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [dynamicTyping, setDynamicTyping] = useState<boolean>(true);
  const [trimValues, setTrimValues] = useState<boolean>(true);
  const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
  const [includeCreateTable, setIncludeCreateTable] = useState<boolean>(true);
  const [singleStatement, setSingleStatement] = useState<boolean>(false);
  const [sqlDialect, setSqlDialect] = useState<string>('sqlite');
  
  // Handle CSV input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCsvInput(e.target.value);
    setError('');
    setIsSqlGenerated(false);
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Update the table name based on the uploaded file
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop(); // Remove extension
      setTableName(nameParts.join('_').toLowerCase().replace(/[^\w_]/g, '_'));
    } else {
      setTableName(file.name.toLowerCase().replace(/[^\w_]/g, '_'));
    }
    
    // Read the file content
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setCsvInput(event.target.result as string);
        setError('');
        setIsSqlGenerated(false);
      }
    };
    reader.onerror = () => {
      setError('Error reading the file');
    };
    reader.readAsText(file);
  };

  // Handle table name change
  const handleTableNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^\w_]/g, '_');
    setTableName(value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleCsv = `Name,Age,Email,IsActive,JoinDate,Score
John Doe,32,john@example.com,true,2021-03-15,85.5
Jane Smith,28,jane@example.com,true,2020-11-20,92.0
Bob Johnson,45,bob@example.com,false,2019-07-08,76.8
Alice Brown,33,alice@example.com,true,2022-01-10,88.2
Charlie Wilson,,charlie@example.com,false,2020-02-28,`;

    setCsvInput(exampleCsv);
    setError('');
    setIsSqlGenerated(false);
    setSqlOutput('');
  };

  // Convert CSV to SQL
  const convertCsvToSql = () => {
    setError('');
    setIsLoading(true);
    setIsSqlGenerated(false);
    setSqlOutput('');
    
    if (!csvInput.trim()) {
      setError('Please enter CSV data');
      setIsLoading(false);
      return;
    }

    try {
      // Generate the SQL
      const options = {
        tableName,
        header: hasHeader,
        delimiter,
        dynamicTyping,
        trimValues,
        skipEmptyLines,
        includeCreateTable,
        singleStatement,
        sqlDialect
      };
      
      const sqlContent = csvToSql(csvInput, options);
      setSqlOutput(sqlContent);
      
      setIsSqlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting CSV to SQL: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setCsvInput('');
    setSqlOutput('');
    setError('');
    setTableName('my_table');
    setIsSqlGenerated(false);
  };

  // Copy SQL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(sqlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download SQL file
  const downloadSql = () => {
    const blob = new Blob([sqlOutput], { type: 'application/sql;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${tableName.replace(/[^\w_]/g, '_')}.sql`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CSV to SQL Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert CSV (Comma-Separated Values) data to SQL INSERT statements. Upload a CSV file or paste your data below.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="csvInput" className="font-medium text-gray-700">
                CSV Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setCsvInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
            
            <textarea
              id="csvInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={csvInput}
              onChange={handleInputChange}
              placeholder="Paste your CSV data here, e.g.,
Name,Age,Email
John Doe,30,john@example.com
Jane Smith,25,jane@example.com"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="tableName" className="block text-sm font-medium text-gray-700 mb-1">
                Table Name
              </label>
              <input
                type="text"
                id="tableName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={tableName}
                onChange={handleTableNameChange}
                placeholder="Enter SQL table name"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="sqlDialect" className="block text-sm font-medium text-gray-700 mb-1">
                SQL Dialect
              </label>
              <select
                id="sqlDialect"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={sqlDialect}
                onChange={(e) => setSqlDialect(e.target.value)}
              >
                <option value="sqlite">SQLite</option>
                <option value="mysql">MySQL</option>
                <option value="postgresql">PostgreSQL</option>
                <option value="sqlserver">SQL Server</option>
              </select>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasHeader"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasHeader" className="ml-2 block text-sm text-gray-700">
                    First row contains headers
                  </label>
                </div>
                
                <div>
                  <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Delimiter
                  </label>
                  <select
                    id="delimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="dynamicTyping"
                    checked={dynamicTyping}
                    onChange={(e) => setDynamicTyping(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="dynamicTyping" className="ml-2 block text-sm text-gray-700">
                    Convert values to proper SQL types
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trimValues"
                    checked={trimValues}
                    onChange={(e) => setTrimValues(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="trimValues" className="ml-2 block text-sm text-gray-700">
                    Trim whitespace from values
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skipEmptyLines"
                    checked={skipEmptyLines}
                    onChange={(e) => setSkipEmptyLines(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="skipEmptyLines" className="ml-2 block text-sm text-gray-700">
                    Skip empty lines
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeCreateTable"
                    checked={includeCreateTable}
                    onChange={(e) => setIncludeCreateTable(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeCreateTable" className="ml-2 block text-sm text-gray-700">
                    Include CREATE TABLE statement
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="singleStatement"
                    checked={singleStatement}
                    onChange={(e) => setSingleStatement(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="singleStatement" className="ml-2 block text-sm text-gray-700">
                    Generate as a single INSERT statement
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertCsvToSql}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to SQL'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              SQL Output
            </label>
            
            {isSqlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy SQL'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadSql}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path                     strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isSqlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    SQL output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isSqlGenerated && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="sqlOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={sqlOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isSqlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">CSV Successfully Converted to SQL!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated SQL for use in your database.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About CSV to SQL Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool converts CSV (Comma-Separated Values) data into SQL statements that can be executed in database systems. The converter parses each row of your CSV file and generates the corresponding INSERT statements according to your selected SQL dialect. You can also include a CREATE TABLE statement that sets up the appropriate schema based on the data types detected in your CSV.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Multiple SQL Dialects</strong> - Support for SQLite, MySQL, PostgreSQL, and SQL Server syntax</li>
              <li><strong>CREATE TABLE Generation</strong> - Automatically creates table schema with appropriate data types</li>
              <li><strong>Data Type Detection</strong> - Intelligently converts CSV values to proper SQL data types</li>
              <li><strong>File Upload</strong> - Upload CSV files directly or paste content manually</li>
              <li><strong>Delimiter Support</strong> - Handle various delimiters (comma, semicolon, tab, pipe)</li>
              <li><strong>Custom Headers</strong> - Use first row as column names or generate default names</li>
              <li><strong>Value Trimming</strong> - Option to trim whitespace from field values</li>
              <li><strong>Empty Line Handling</strong> - Skip or include empty lines as needed</li>
              <li><strong>Statement Format</strong> - Generate individual INSERT statements or a single multi-row INSERT</li>
              <li><strong>Quoted Value Support</strong> - Properly handles quoted fields in CSV</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">SQL Dialect Considerations</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>SQLite</strong> - Uses square brackets for identifiers, simple type system (TEXT, INTEGER, NUMERIC)</li>
              <li><strong>MySQL</strong> - Uses backticks for identifiers, has specific date/time types</li>
              <li><strong>PostgreSQL</strong> - Uses double quotes for identifiers, supports native boolean type</li>
              <li><strong>SQL Server</strong> - Uses square brackets for identifiers, has specific T-SQL syntax</li>
              <li>String escaping and NULL handling are adapted to each dialect's requirements</li>
              <li>Column name sanitization ensures compatibility with each database system</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">SQL Type Mapping</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Empty string values are converted to NULL</li>
              <li>Integer values (e.g., 42) are mapped to INTEGER type</li>
              <li>Decimal values (e.g., 3.14) are mapped to NUMERIC or similar types</li>
              <li>Date values in ISO format (e.g., 2023-01-15) are mapped to date/time types based on dialect</li>
              <li>Boolean values (true/false, 1/0) are handled according to dialect support</li>
              <li>Text values are properly escaped according to SQL string requirements</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">CSV Best Practices</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use consistent delimiter throughout the file</li>
              <li>Include a header row for better column naming</li>
              <li>Use double quotes around values that contain delimiters or newlines</li>
              <li>Escape double quotes within quoted fields by doubling them (e.g., "He said ""Hello"" to me")</li>
              <li>Use ISO date formats (YYYY-MM-DD) for better type detection</li>
              <li>Keep column count consistent across all rows</li>
              <li>For SQL compatibility, avoid column names that are reserved SQL keywords</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Usage Tips</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Review the generated SQL before executing in your database system</li>
              <li>For large datasets, consider generating SQL in batches</li>
              <li>Test the SQL on a development or test database first</li>
              <li>Consider database-specific optimizations for bulk imports (e.g., COPY in PostgreSQL)</li>
              <li>Adjust the table name to follow your database naming conventions</li>
              <li>For very large files, consider server-side processing or direct database import tools</li>
              <li>Verify data types are correctly mapped, especially for date/time and numeric values</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Common Issues and Solutions</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Column name conflicts</strong> - The converter sanitizes names to avoid SQL syntax issues</li>
              <li><strong>Type inference errors</strong> - Use the dynamic typing option to detect appropriate types</li>
              <li><strong>Special characters</strong> - Values are properly escaped based on SQL dialect</li>
              <li><strong>Large files</strong> - For very large datasets, consider processing in chunks</li>
              <li><strong>Complex CSV</strong> - For CSVs with nested structures, preprocessing may be needed</li>
              <li><strong>NULL values</strong> - Empty values are converted to SQL NULL by default</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvToSql;